import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ndfApi } from '../../services/ndf-api';
import type { AdresseTrajet, ApercuTrajet, TrajetRecent } from '../../types/ndf';
import {
  BoutonPrincipal,
  BoutonSecondaire,
  Case,
  Champ,
  Saisie,
  Selection,
} from '../../components/ndf/ui/Champ';
import { erreurGlobale, erreursParChamp, type ErreursChamps } from '../../components/ndf/ui/erreurs-champs';
import { MessageErreur } from '../../components/ndf/ui/MessageEtat';
import { ChoixAdresse } from '../../components/ndf/ChoixAdresse';
import { RattachementClient } from '../../components/ndf/RattachementClient';
import { useToast } from '../../context/ToastContext';

/**
 * Déclaration d'un trajet en véhicule personnel (specs §D).
 *
 * La distance ne se saisit PAS : elle est calculée à partir des adresses. Un
 * kilométrage tapé au clavier ne se contrôle pas, et la différence entre 180 et
 * 210 km ne se lit sur aucun justificatif.
 *
 * Le montant non plus ne se saisit pas : il vient du barème officiel, de la
 * puissance fiscale du véhicule et du cumul annuel déjà parcouru. Il est
 * recalculé côté serveur à l'enregistrement — l'aperçu ne fait que le montrer.
 */
export function NdfTrajetFormPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const qc = useQueryClient();

  const contexte = useQuery({
    queryKey: ['ndf', 'trajet', 'contexte'],
    queryFn: () => ndfApi.trajets.contexte(),
    retry: false,
  });

  const [f, setF] = useState({
    date_depense: new Date().toLocaleDateString('sv-SE'),
    vehicule_id: '',
    nature_deplacement: '',
    description: '',
    aller_retour: true,
    facturable: false,
    client_crm_id: null as string | null,
    client_nom: null as string | null,
    mission_crm_id: null as string | null,
    mission_nom: null as string | null,
    type_mission: null as string | null,
  });
  const [etapes, setEtapes] = useState<(AdresseTrajet | null)[]>([null, null]);
  const [apercu, setApercu] = useState<ApercuTrajet | null>(null);
  const [erreurs, setErreurs] = useState<ErreursChamps>({});
  const [nomFavori, setNomFavori] = useState<AdresseTrajet | null>(null);
  const [libelleCourt, setLibelleCourt] = useState('');

  const favoris = contexte.data?.favoris ?? [];
  const recents = contexte.data?.recents ?? [];

  const enregistrerFavori = useMutation({
    mutationFn: () =>
      ndfApi.trajets.ajouterFavori({
        libelle_court: libelleCourt.trim(),
        libelle: nomFavori!.libelle,
        latitude: nomFavori!.latitude,
        longitude: nomFavori!.longitude,
      }),
    onSuccess: () => {
      toast.success('Adresse enregistrée.');
      setNomFavori(null);
      setLibelleCourt('');
      qc.invalidateQueries({ queryKey: ['ndf', 'trajet', 'contexte'] });
    },
    onError: (err) => toast.error(erreurGlobale(err) ?? 'L’adresse n’a pas pu être enregistrée.'),
  });

  /** Rejouer un trajet déjà fait : c'est le geste du consultant qui revient. */
  const recommencer = (t: TrajetRecent) => {
    setEtapes(t.etapes);
    setF((v) => ({
      ...v,
      aller_retour: t.aller_retour,
      nature_deplacement: t.nature_deplacement ?? v.nature_deplacement,
    }));
  };

  // Un seul véhicule actif : il est choisi d'office. Faire cliquer pour un
  // choix unique, c'est une seconde perdue à chaque trajet.
  const vehicules = contexte.data?.vehicules ?? [];
  useEffect(() => {
    if (!f.vehicule_id && vehicules.length === 1) {
      setF((v) => ({ ...v, vehicule_id: vehicules[0]!.id }));
    }
  }, [vehicules, f.vehicule_id]);

  const completes = etapes.filter((e): e is AdresseTrajet => e !== null);
  const pret = completes.length >= 2 && completes.length === etapes.length && f.vehicule_id !== '';

  const calculer = useMutation({
    mutationFn: () =>
      ndfApi.trajets.apercu({
        date_depense: f.date_depense,
        vehicule_id: f.vehicule_id,
        etapes: completes,
        aller_retour: f.aller_retour,
      }),
    onSuccess: setApercu,
    onError: (err) => {
      setApercu(null);
      toast.error(erreurGlobale(err) ?? 'La distance n’a pas pu être calculée.');
    },
  });

  // L'aperçu se refait dès qu'une donnée qui l'influence change : un montant
  // affiché qui ne correspond plus aux adresses à l'écran serait pire que pas
  // de montant du tout.
  useEffect(() => {
    setApercu(null);
    if (pret) calculer.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(completes), f.aller_retour, f.vehicule_id, f.date_depense]);

  const enregistrer = useMutation({
    mutationFn: () =>
      ndfApi.trajets.creer({
        date_depense: f.date_depense,
        vehicule_id: f.vehicule_id,
        etapes: completes,
        aller_retour: f.aller_retour,
        nature_deplacement: f.nature_deplacement || null,
        description: f.description || null,
        facturable: f.facturable,
        client_crm_id: f.client_crm_id,
        client_nom: f.client_nom,
        mission_crm_id: f.mission_crm_id,
        mission_nom: f.mission_nom,
        type_mission: f.type_mission,
      }),
    onSuccess: () => {
      toast.success('Trajet enregistré.');
      qc.invalidateQueries({ queryKey: ['ndf', 'depenses'] });
      qc.invalidateQueries({ queryKey: ['ndf', 'trajet', 'contexte'] });
      navigate('/notes-de-frais/mes-depenses');
    },
    onError: (err) => {
      setErreurs(erreursParChamp(err));
      const g = erreurGlobale(err);
      if (g) toast.error(g);
    },
  });

  if (contexte.isLoading) return <p className="text-sm text-nuit/60">Chargement…</p>;
  if (contexte.error) return <MessageErreur erreur={contexte.error} />;

  // R-IK-02 : sans véhicule renseigné, aucune indemnité n'est calculable — le
  // barème dépend de caractéristiques qu'on ne peut pas deviner.
  if (vehicules.length === 0) {
    return (
      <div className="space-y-3">
        <h1 className="font-titre text-lg font-bold text-nuit">Déclarer un trajet</h1>
        <p className="rounded-md border border-jaune-fond/60 border-l-4 border-l-jaune-fond p-3 text-sm text-jaune">
          Aucun véhicule n’est enregistré sur votre fiche. Le barème kilométrique dépend du type de
          véhicule, de sa puissance fiscale et de sa motorisation : demandez à la RH de les
          renseigner, puis revenez ici.
        </p>
        <BoutonSecondaire type="button" onClick={() => navigate('/notes-de-frais/mes-depenses')}>
          Retour à mes dépenses
        </BoutonSecondaire>
      </div>
    );
  }

  return (
    <form
      className="space-y-4 pb-4"
      onSubmit={(e) => {
        e.preventDefault();
        enregistrer.mutate();
      }}
    >
      <header className="flex items-center justify-between gap-2">
        <h1 className="font-titre text-lg font-bold text-nuit">Déclarer un trajet</h1>
        <BoutonSecondaire type="button" onClick={() => navigate(-1)}>
          Annuler
        </BoutonSecondaire>
      </header>

      {/* R-IK-01 : la règle est rappelée là où elle s'applique, pas dans un
          document que personne n'ouvrira. */}
      <p className="rounded-md border border-ciel/60 border-l-4 border-l-ciel p-3 text-sm text-nuit">
        Le kilométrage est calculé sur l’itinéraire le plus rapide entre les adresses saisies -
        celui que vous empruntez réellement. Vous avez déclaré{' '}
        <strong>{contexte.data?.cumul_km ?? 0} km</strong> en {contexte.data?.annee}.
      </p>

      <section className="space-y-4 rounded-lg border border-nuit/10 bg-white p-4">
        <Champ label="Date du trajet" requis erreur={erreurs.date_depense}>
          <Saisie
            type="date"
            required
            value={f.date_depense}
            onChange={(e) => setF({ ...f, date_depense: e.target.value })}
          />
        </Champ>

        {vehicules.length > 1 && (
          <Champ label="Véhicule" requis erreur={erreurs.vehicule_id}>
            <Selection
              required
              value={f.vehicule_id}
              onChange={(e) => setF({ ...f, vehicule_id: e.target.value })}
            >
              <option value="">Choisir…</option>
              {vehicules.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.immatriculation ?? v.type_vehicule} · {v.cv_fiscaux ?? '-'} CV ·{' '}
                  {v.motorisation}
                </option>
              ))}
            </Selection>
          </Champ>
        )}
      </section>

      {/* « Recommencer ce trajet » : un consultant refait le même aller-retour
          toutes les semaines. Le retaper est le premier gaspillage à supprimer. */}
      {recents.length > 0 && etapes.every((e) => e === null) && (
        <section className="rounded-lg border border-nuit/10 bg-white p-4">
          <h2 className="mb-2 font-titre text-sm font-bold text-nuit">Recommencer un trajet</h2>
          <ul className="space-y-1">
            {recents.map((t) => (
              <li key={t.id}>
                <button
                  type="button"
                  onClick={() => recommencer(t)}
                  className="min-h-11 w-full text-left text-sm text-nuit"
                >
                  {t.etapes[0]?.libelle} → {t.etapes[t.etapes.length - 1]?.libelle}
                  {t.aller_retour ? ' (A/R)' : ''} · {t.distance_km} km
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="space-y-3 rounded-lg border border-nuit/10 bg-white p-4">
        <h2 className="font-titre text-sm font-bold text-nuit">Itinéraire</h2>

        {etapes.map((etape, i) => (
          <ChoixAdresse
            key={i}
            label={i === 0 ? 'Départ' : i === etapes.length - 1 ? 'Arrivée' : `Étape ${i}`}
            adresse={etape}
            favoris={favoris}
            onEnregistrerFavori={(a) => {
              setNomFavori(a);
              // On propose la commune comme nom court : c'est ce qu'on
              // reconnaît le plus vite dans une liste de pastilles.
              setLibelleCourt(a.libelle.split(' ').slice(-1)[0] ?? '');
            }}
            onChange={(a) => setEtapes(etapes.map((e, j) => (j === i ? a : e)))}
            onRetirer={
              // On ne retire ni le départ ni l'arrivée : un trajet en a
              // toujours un de chaque.
              etapes.length > 2 && i > 0 && i < etapes.length - 1
                ? () => setEtapes(etapes.filter((_, j) => j !== i))
                : undefined
            }
          />
        ))}

        <BoutonSecondaire
          type="button"
          onClick={() => setEtapes([...etapes.slice(0, -1), null, etapes[etapes.length - 1]!])}
        >
          Ajouter une étape
        </BoutonSecondaire>

        <Case
          label="Aller-retour"
          aide="Le retour emprunte le même itinéraire."
          checked={f.aller_retour}
          onChange={(e) => setF({ ...f, aller_retour: e.target.checked })}
        />
      </section>

      {/* Le résultat du calcul : tout y est en lecture seule, y compris la
          distance : c'est précisément ce qui ne doit pas se saisir. */}
      <section className="rounded-lg border border-nuit/10 bg-white p-4">
        <h2 className="mb-2 font-titre text-sm font-bold text-nuit">Indemnité</h2>
        {calculer.isPending && <p className="text-sm text-nuit/60">Calcul de l’itinéraire…</p>}
        {!calculer.isPending && !apercu && (
          <p className="text-sm text-nuit/60">
            Choisissez un départ et une arrivée pour voir la distance et le montant.
          </p>
        )}
        {apercu && (
          <div className="space-y-1 text-sm">
            <Ligne
              libelle="Distance"
              valeur={`${apercu.distance_km} km${
                apercu.duree_min ? ` · environ ${dureeLisible(apercu.duree_min)}` : ''
              }`}
            />
            <Ligne libelle="Barème appliqué" valeur={apercu.formule} />
            <Ligne
              libelle="Montant"
              valeur={apercu.montant.toLocaleString('fr-FR', {
                style: 'currency',
                currency: 'EUR',
              })}
            />
            <Ligne
              libelle={`Cumul ${contexte.data?.annee}`}
              valeur={`${apercu.cumul_apres_km} km`}
            />
            {apercu.alerte_cumul && (
              <p className="mt-2 rounded-md border border-jaune-fond/60 border-l-4 border-l-jaune-fond p-3 text-sm text-jaune">
                Vous dépasserez {contexte.data?.seuil_alerte_km} km déclarés cette année. Ce n’est
                pas un plafond, mais la RH pourra vous demander des précisions.
              </p>
            )}
          </div>
        )}
      </section>

      {nomFavori && (
        <section className="space-y-2 rounded-lg border border-ciel bg-white p-4">
          <Champ label="Nom court de cette adresse" requis aide={nomFavori.libelle}>
            <Saisie
              autoFocus
              value={libelleCourt}
              placeholder="Bureau, Domicile, APRR Dijon…"
              onChange={(e) => setLibelleCourt(e.target.value)}
            />
          </Champ>
          <div className="flex flex-wrap gap-2">
            <BoutonSecondaire type="button" onClick={() => setNomFavori(null)}>
              Annuler
            </BoutonSecondaire>
            <BoutonPrincipal
              type="button"
              disabled={!libelleCourt.trim() || enregistrerFavori.isPending}
              onClick={() => enregistrerFavori.mutate()}
            >
              Enregistrer l’adresse
            </BoutonPrincipal>
          </div>
        </section>
      )}

      <section className="space-y-3 rounded-lg border border-nuit/10 bg-white p-4">
        <Champ label="Motif du déplacement">
          <Saisie
            value={f.nature_deplacement}
            placeholder="Rendez-vous client, formation…"
            onChange={(e) => setF({ ...f, nature_deplacement: e.target.value })}
          />
        </Champ>

        <Case
          label="Refacturable à un client"
          checked={f.facturable}
          onChange={(e) => setF({ ...f, facturable: e.target.checked })}
        />
        {f.facturable && (
          <div className="border-t border-nuit/10 pt-3">
            <RattachementClient
              clientId={f.client_crm_id}
              clientNom={f.client_nom}
              missionId={f.mission_crm_id}
              onChange={(v) => setF({ ...f, ...v })}
            />
          </div>
        )}
      </section>

      <div className="sticky bottom-24 flex gap-2 md:bottom-0">
        <BoutonSecondaire type="button" onClick={() => navigate(-1)}>
          Annuler
        </BoutonSecondaire>
        <BoutonPrincipal type="submit" disabled={!apercu || enregistrer.isPending} className="flex-1">
          {enregistrer.isPending ? 'Enregistrement…' : 'Enregistrer le trajet'}
        </BoutonPrincipal>
      </div>
    </form>
  );
}

/** « 2 h 05 » se lit d'un coup d'œil, « 125 min » demande un calcul. */
function dureeLisible(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return h ? `${h} h ${String(m).padStart(2, '0')}` : `${m} min`;
}

function Ligne({ libelle, valeur }: { libelle: string; valeur: string }) {
  return (
    <div className="flex justify-between gap-4 border-b border-nuit/5 pb-1 last:border-0">
      <span className="text-nuit/60">{libelle}</span>
      <span className="text-right font-medium text-nuit">{valeur}</span>
    </div>
  );
}
